import React, { useState, useEffect, useRef } from 'react';
import { Search, Plus, X, Save, Loader2,  Trash2} from 'lucide-react';
import { getRequest, postRequest } from "../../services/apiService";
import { ToastContainer, toast } from 'react-toastify';
import 'react-toastify/dist/ReactToastify.css';
import ReusableDeleteDialog from '../../common/ReusableDeleteDialog';

const EquipmentActivityMapping = ({ equipmentId }) => {
  const [activities, setActivities] = useState([]);
  const [equipmentActivities, setEquipmentActivities] = useState([]);
  const [originalEquipmentActivities, setOriginalEquipmentActivities] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [hasChanges, setHasChanges] = useState(false);
  const [savingChanges, setSavingChanges] = useState(false);
  
  // Dropdown states
  const [searchTerm, setSearchTerm] = useState('');
  const [isActivitiesDropdownOpen, setIsActivitiesDropdownOpen] = useState(false);
  const [selectedActivity, setSelectedActivity] = useState('');
  const [selectedSequence, setSelectedSequence] = useState('');
  
  // Add activity states
  const [addActivityMode, setAddActivityMode] = useState(false);
  const [newActivityName, setNewActivityName] = useState('');
  const [newActivityDescription, setNewActivityDescription] = useState('');
  const [addActivityLoading, setAddActivityLoading] = useState(false);
  const [errorMessage, setErrorMessage] = useState('');
  
  // Refs for handling outside clicks
  const activitiesDropdownRef = useRef(null);
  
  // Drag and drop state
  const [draggedItemIndex, setDraggedItemIndex] = useState(null);
  const [deleteActivityLoading, setDeleteActivityLoading] = useState(null);
  const [deleteDialogVisible, setDeleteDialogVisible] = useState(false);
  const [selectedActivityToDelete, setSelectedActivityToDelete] = useState(null);
  const [deleteFormData, setDeleteFormData] = useState({ confirmation: '' });
  
  const deleteFormFields = [
    {
      name: "confirmation",
      label: "Confirmation",
      type: "text",
      required: true,
      placeholder: "Type 'DELETE' to confirm",
      helpText: "This action cannot be undone. Type 'DELETE' to confirm deletion."
    }
  ];

  // Function to format the date
  const formatDate = (dateString) => {
    const date = new Date(dateString);
    return `${date.getDate()} ${date.toLocaleString('default', { month: 'short' })}, ${date.getFullYear()}`;
  };

  // Generate vibrant colors for activity boxes
  const generateBoxColor = (index) => {
    const colors = [
      'bg-blue-100 border-blue-300',
      'bg-purple-100 border-purple-300',
      'bg-green-100 border-green-300',
      'bg-yellow-100 border-yellow-300',
      'bg-pink-100 border-pink-300',
      'bg-indigo-100 border-indigo-300',
      'bg-red-100 border-red-300',
      'bg-teal-100 border-teal-300',
      'bg-orange-100 border-orange-300',
      'bg-cyan-100 border-cyan-300'
    ];
    return colors[index % colors.length];
  };

  // Generate dot colors for timeline
  const generateDotColor = (index) => {
    const colors = [
      'bg-blue-500',
      'bg-purple-500',
      'bg-green-500',
      'bg-yellow-500',
      'bg-pink-500',
      'bg-indigo-500',
      'bg-red-500',
      'bg-teal-500',
      'bg-orange-500',
      'bg-cyan-500'
    ];
    return colors[index % colors.length];
  };

  // Dynamic icon based on activity name
  const getActivityIcon = (activityName) => {
    const baseClasses = "h-5 w-5 text-gray-700";
    
    // Map activity names to specific icons
    const activityNameLower = activityName.toLowerCase();
    
    if (activityNameLower.includes('alignment') || activityNameLower.includes('installation')) {
      return (
        <svg xmlns="http://www.w3.org/2000/svg" className={baseClasses} fill="none" viewBox="0 0 24 24" stroke="currentColor">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M10 6H6a2 2 0 00-2 2v10a2 2 0 002 2h10a2 2 0 002-2v-4M14 4h6m0 0v6m0-6L10 14" />
        </svg>
      );
    } else if (activityNameLower.includes('check')) {
      return (
        <svg xmlns="http://www.w3.org/2000/svg" className={baseClasses} fill="none" viewBox="0 0 24 24" stroke="currentColor">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5H7a2 2 0 00-2 2v12a2 2 0 002 2h10a2 2 0 002-2V7a2 2 0 00-2-2h-2M9 5a2 2 0 002 2h2a2 2 0 002-2M9 5a2 2 0 012-2h2a2 2 0 012 2m-6 9l2 2 4-4" />
        </svg>
      );
    } else if (activityNameLower.includes('test') || activityNameLower.includes('trial')) {
      return (
        <svg xmlns="http://www.w3.org/2000/svg" className={baseClasses} fill="none" viewBox="0 0 24 24" stroke="currentColor">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m5.618-4.016A11.955 11.955 0 0112 2.944a11.955 11.955 0 01-8.618 3.04A12.02 12.02 0 003 9c0 5.591 3.824 10.29 9 11.622 5.176-1.332 9-6.03 9-11.622 0-1.042-.133-2.052-.382-3.016z" />
        </svg>
      );
    }
    
    // Default icon if no specific match
    return (
      <svg xmlns="http://www.w3.org/2000/svg" className={baseClasses} fill="none" viewBox="0 0 24 24" stroke="currentColor">
        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 6v6m0 0v6m0-6h6m-6 0H6" />
      </svg>
    );
  };

  // Show toast notification using react-toastify
  const showToast = (severity, detail) => {
    if (severity === 'success') {
      toast.success(detail, {
        position: "top-right",
        autoClose: 3000,
        hideProgressBar: false,
        closeOnClick: true,
        pauseOnHover: true,
        draggable: true,
      });
    } else {
      toast.error(detail, {
        position: "top-right",
        autoClose: 3000,
        hideProgressBar: false,
        closeOnClick: true,
        pauseOnHover: true,
        draggable: true,
      });
    }
  };

  // Fetch equipment activities from API
  useEffect(() => {
    const fetchEquipmentActivities = async () => {
      setLoading(true);
      try {
        const response = await getRequest(`master/equipment-activity-mapping/?equipment_id=${equipmentId}&dropdown=true`);
        // Sort activities by sequence
        const sortedActivities = response.sort((a, b) => a.sequence - b.sequence);
        setEquipmentActivities(sortedActivities);
        setOriginalEquipmentActivities(JSON.parse(JSON.stringify(sortedActivities))); // Deep copy for tracking changes
      } catch (err) {
        console.error("Error fetching equipment activities:", err);
        setError("Failed to load equipment activities");
      } finally {
        setLoading(false);
      }
    };

    if (equipmentId) {
      fetchEquipmentActivities();
    }
  }, [equipmentId]);

  // Fetch all available activities for dropdown
  const fetchActivities = async () => {
  try {
    let allActivities = [];
    let nextUrl = 'master/equipment-activity/';

    while (nextUrl) {
      const response = await getRequest(nextUrl);
      // Handle both potential response structures
      const pageData = response.results?.data || response.data || [];
      const paginationNext = response.results?.next || response.next;

      allActivities = [...allActivities, ...pageData];
      nextUrl = paginationNext;
    }

    const activityList = allActivities.map(item => item.name);
    setActivities(activityList);
  } catch (error) {
    console.error("Error fetching activities:", error);
    showToast('error', 'Failed to fetch activities');
  }
};

  useEffect(() => {
    fetchActivities();
  }, []);

  // Handle clicking outside dropdowns
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (activitiesDropdownRef.current && !activitiesDropdownRef.current.contains(event.target)) {
        setIsActivitiesDropdownOpen(false);
        // Reset add activity mode when dropdown closes
        if (addActivityMode) {
          setAddActivityMode(false);
          setNewActivityName('');
          setNewActivityDescription('');
          setErrorMessage('');
        }
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [addActivityMode]);

  // Filter activities based on search term
  const filteredActivities = activities.filter(activity => 
    activity.toLowerCase().includes(searchTerm.toLowerCase())
  );

  // Check if search term doesn't match any existing activities
  const searchTermNotFound = searchTerm.trim() !== '' && 
    filteredActivities.length === 0 && 
    !activities.some(activity => activity.toLowerCase() === searchTerm.toLowerCase());

  // Handle activity selection
  const handleActivitySelect = (activity) => {
    setSelectedActivity(activity);
    setIsActivitiesDropdownOpen(false);
  };

  // Handle add activity mode toggle
  const toggleAddActivityMode = () => {
    setAddActivityMode(true);
    setNewActivityName(searchTerm);
    setErrorMessage('');
  };

  // Handle add new activity
  const handleAddNewActivity = async () => {
    if (!newActivityName.trim()) {
      setErrorMessage('Activity name is required');
      return;
    }

    // Check if activity name already exists
    if (activities.some(activity => activity.toLowerCase() === newActivityName.toLowerCase())) {
      setErrorMessage('Activity with this name already exists');
      return;
    }

    setAddActivityLoading(true);
    try {
      // API call to add new activity
      await postRequest("master/equipment-activity/", {
        name: newActivityName,
        description: newActivityDescription,
        status_db: 1
      });

      // Refresh activities list
      await fetchActivities();
      
      // Set the newly added activity as selected
      setSelectedActivity(newActivityName);
      
      // Exit add mode and close dropdown
      setAddActivityMode(false);
      setIsActivitiesDropdownOpen(false);
      setNewActivityName('');
      setNewActivityDescription('');
      
      // Show success message
      showToast('success', 'Activity added successfully');
    } catch (error) {
      console.error("Error adding activity:", error);
      setErrorMessage('Failed to add activity. Please try again.');
    } finally {
      setAddActivityLoading(false);
    }
  };

  // Handle cancel add new activity
  const handleCancelAddActivity = () => {
    setAddActivityMode(false);
    setNewActivityName('');
    setNewActivityDescription('');
    setErrorMessage('');
  };

  // Drag and Drop handlers
  const handleDragStart = (index) => {
    setDraggedItemIndex(index);
  };

  const handleDragOver = (e) => {
    e.preventDefault();
  };

  const handleDrop = (dropIndex) => {
    if (draggedItemIndex === null) return;
    
    const newActivities = [...equipmentActivities];
    const draggedItem = newActivities[draggedItemIndex];
    
    // Remove the dragged item
    newActivities.splice(draggedItemIndex, 1);
    
    // Insert it at the drop position
    newActivities.splice(dropIndex, 0, draggedItem);
    
    // Update sequence numbers
    newActivities.forEach((activity, idx) => {
      activity.sequence = idx + 1;
    });
    
    setEquipmentActivities(newActivities);
    setDraggedItemIndex(null);
    setHasChanges(true);
  };

  // Function to save sequence changes
  const handleSaveSequenceChanges = async () => {
    setSavingChanges(true);
    try {
      // Prepare payload for API with status_db included
      const payload = equipmentActivities.map(activity => ({
        id: activity.id,
        equipment_activity: activity.equipment_activity.id,
        sequence: activity.sequence,
        status_db: activity.status_db || 1
      }));
      
      // API call to update sequences using POST
      await postRequest(`master/equipment-activity-mapping/${equipmentId}/`, payload);
      
      // Update original activities to match current state
      setOriginalEquipmentActivities(JSON.parse(JSON.stringify(equipmentActivities)));
      setHasChanges(false);
      
      // Show success message
      showToast('success', 'Activities sequence updated successfully');
    } catch (error) {
      console.error("Error updating activity sequences:", error);
      showToast('error', 'Failed to update activity sequences');
    } finally {
      setSavingChanges(false);
    }
  };

  // Handle adding new equipment activity
  const handleAddEquipmentActivity = async () => {
    if (!selectedActivity) {
      showToast('error', 'Please select an activity');
      return;
    }
    
    setSavingChanges(true);
    try {
      // Find the activity ID from the selected activity name
      const activityResponse = await getRequest('master/equipment-activity/');
      const activityData = activityResponse.results?.data.find(item => item.name === selectedActivity);
      
      if (!activityData) {
        showToast('error', 'Selected activity not found');
        setSavingChanges(false);
        return;
      }
      
      // Create new sequence number (next in order)
      const newSequence = equipmentActivities.length > 0 
        ? Math.max(...equipmentActivities.map(item => item.sequence)) + 1
        : 1;
      
      // Prepare payload for API with status_db included
      const existingActivities = equipmentActivities.map(activity => ({
        id: activity.id,
        equipment_activity: activity.equipment_activity.id,
        sequence: activity.sequence,
        status_db: activity.status_db || 1
      }));
      
      const payload = [
        ...existingActivities,
        {
          id: null,
          equipment_activity: activityData.id,
          sequence: newSequence,
          status_db: 1 // New records are active
        }
      ];
      
      // API call to update with new activity using POST
      await postRequest(`master/equipment-activity-mapping/${equipmentId}/`, payload);
      
      // Refresh equipment activities
      const response = await getRequest(`master/equipment-activity-mapping/?equipment_id=${equipmentId}&dropdown=true`);
      const sortedActivities = response.sort((a, b) => a.sequence - b.sequence);
      setEquipmentActivities(sortedActivities);
      setOriginalEquipmentActivities(JSON.parse(JSON.stringify(sortedActivities)));
      
      // Reset selected activity
      setSelectedActivity('');
      
      // Show success message
      showToast('success', 'Activity added successfully');
    } catch (error) {
      console.error("Error adding equipment activity:", error);
      showToast('error', 'Failed to add activity');
    } finally {
      setSavingChanges(false);
    }
  };

  // Handle soft delete activity with status_db: 3
  const handleDeleteActivity = async () => {
    if (!selectedActivityToDelete) return;

    setDeleteActivityLoading(selectedActivityToDelete);
    
    try {
      // Find the activity to soft delete
      const activityToDelete = equipmentActivities.find(
        activity => activity.id === selectedActivityToDelete
      );
      
      // Prepare payload with soft delete status
      const payload = equipmentActivities.map(activity => {
        if (activity.id === selectedActivityToDelete) {
          return {
            id: activity.id,
            equipment_activity: activity.equipment_activity.id,
            sequence: activity.sequence,
            status_db: 3 // Soft delete status
          };
        }
        return {
          id: activity.id,
          equipment_activity: activity.equipment_activity.id,
          sequence: activity.sequence,
          status_db: activity.status_db || 1
        };
      });
      
      // API call to soft delete using POST
      await postRequest(`master/equipment-activity-mapping/${equipmentId}/`, payload);
      
      // Update local state by removing the soft-deleted activity
      const updatedActivities = equipmentActivities.filter(
        activity => activity.id !== selectedActivityToDelete
      );
      
      // Recalculate sequences
      updatedActivities.forEach((activity, idx) => {
        activity.sequence = idx + 1;
      });
      
      setEquipmentActivities(updatedActivities);
      setOriginalEquipmentActivities(JSON.parse(JSON.stringify(updatedActivities)));
      
      // Show success message
      showToast('success', 'Activity removed successfully');
    } catch (error) {
      console.error("Error deleting activity:", error);
      showToast('error', 'Failed to remove activity');
    } finally {
      setDeleteActivityLoading(null);
      setDeleteDialogVisible(false);
      setSelectedActivityToDelete(null);
      setDeleteFormData({ confirmation: '' });
    }
  };

  if (loading) {
    return (
      <div className="flex justify-center items-center h-64">
        <Loader2 className="h-8 w-8 text-blue-500 animate-spin" />
        <span className="ml-2 text-gray-600">Loading activities...</span>
      </div>
    );
  }

  if (error) {
    return (
      <div className="p-4 bg-red-50 border border-red-200 rounded-md text-red-600">
        {error}
      </div>
    );
  }

  return (
    <div className="p-4 bg-gray-50 min-h-screen">
      {/* Toast container for notifications */}
      <ToastContainer />
      <ReusableDeleteDialog
        visible={deleteDialogVisible}
        onHide={() => {
          setDeleteDialogVisible(false);
          setSelectedActivityToDelete(null);
        }}
        onDelete={handleDeleteActivity}
        fields={deleteFormFields}
        formData={deleteFormData}
        setFormData={setDeleteFormData}
        loading={deleteActivityLoading}
        title="Delete Equipment Activity Mapping"
        description={`Are you sure you want to delete mapping for activity "${equipmentActivities.find(a => a.id === selectedActivityToDelete)?.equipment_activity?.name}"? This action cannot be undone. Type 'DELETE' to confirm.`}
        deleteLabel="Delete"
      />
      
      <div className="max-w-6xl mx-auto">
        <h2 className="text-2xl font-bold mb-6 text-gray-800">Equipment Activity Mapping</h2>
        
        {/* Actions Section */}
        <div className="mb-6 flex flex-col md:flex-row gap-4">
          {/* Activities Dropdown */}
          <div className="relative w-full md:w-1/2" ref={activitiesDropdownRef}>
            <div className="text-gray-700 mb-2 font-medium">Select Activity</div>
            <div 
              className="border rounded-lg bg-white p-2 flex justify-between items-center cursor-pointer shadow-sm"
              onClick={() => !addActivityMode && setIsActivitiesDropdownOpen(!isActivitiesDropdownOpen)}
            >
              <div className="flex-1 truncate">
                {selectedActivity || "Select an activity..."}
              </div>
              <div className="relative">
                {loading ? (
                  <Loader2 className="h-5 w-5 text-gray-400 animate-spin" />
                ) : (
                  <Search className="h-5 w-5 text-gray-400" />
                )}
                <input
                  type="text"
                  className="absolute inset-0 w-full opacity-0 cursor-pointer"
                  onClick={(e) => {
                    e.stopPropagation();
                    setIsActivitiesDropdownOpen(true);
                  }}
                  onChange={(e) => {
                    e.stopPropagation();
                    setSearchTerm(e.target.value);
                    if (addActivityMode) {
                      setNewActivityName(e.target.value);
                    }
                  }}
                  value={addActivityMode ? newActivityName : searchTerm}
                />
              </div>
            </div>
            
            {isActivitiesDropdownOpen && !addActivityMode && (
              <div className="absolute z-10 w-full mt-1 bg-white border rounded-lg shadow-lg max-h-60 overflow-y-auto">
                <div className="p-2 border-b">
                  <input
                    type="text"
                    className="w-full p-2 border rounded focus:outline-none focus:ring-2 focus:ring-blue-500"
                    placeholder="Search activities..."
                    value={searchTerm}
                    onChange={(e) => setSearchTerm(e.target.value)}
                    onClick={(e) => e.stopPropagation()}
                  />
                </div>
                <div>
                  {loading ? (
                    <div className="p-4 text-center">
                      <Loader2 className="h-6 w-6 text-blue-500 animate-spin mx-auto" />
                      <p className="mt-2 text-gray-500">Loading activities...</p>
                    </div>
                  ) : (
                    <>
                      {filteredActivities.length > 0 ? (
                        filteredActivities.map((activity, index) => (
                          <div
                            key={index}
                            className="p-2 hover:bg-gray-100 cursor-pointer"
                            onClick={() => handleActivitySelect(activity)}
                          >
                            {activity}
                          </div>
                        ))
                      ) : (
                        <div className="p-2 text-gray-500 text-center">No activities found</div>
                      )}
                      
                      {/* Add activity option */}
                      {searchTermNotFound && (
                        <div 
                          className="p-2 border-t flex items-center justify-center bg-blue-50 hover:bg-blue-100 cursor-pointer text-blue-600"
                          onClick={toggleAddActivityMode}
                        >
                          <Plus className="h-4 w-4 mr-2" />
                          <span>Add "{searchTerm}" as new activity</span>
                        </div>
                      )}
                    </>
                  )}
                </div>
              </div>
            )}
            
            {/* Add Activity Form */}
            {isActivitiesDropdownOpen && addActivityMode && (
              <div className="absolute z-10 w-full mt-1 bg-white border rounded-lg shadow-lg overflow-visible">
                <div className="p-3 border-b">
                  <div className="flex justify-between items-center mb-3">
                    <h3 className="font-medium text-gray-800">Add New Activity</h3>
                    <button 
                      className="text-gray-500 hover:text-gray-700"
                      onClick={handleCancelAddActivity}
                    >
                      <X className="h-5 w-5" />
                    </button>
                  </div>
                  
                  {errorMessage && (
                    <div className="mb-3 p-2 bg-red-50 border border-red-200 rounded text-red-600 text-sm">
                      {errorMessage}
                    </div>
                  )}
                  
                  <div className="mb-3">
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Activity Name
                    </label>
                    <input
                      type="text"
                      className="w-full p-2 border rounded focus:outline-none focus:ring-2 focus:ring-blue-500"
                      value={newActivityName}
                      onChange={(e) => setNewActivityName(e.target.value)}
                      placeholder="Enter activity name"
                    />
                  </div>
                  
                  <div className="mb-4">
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Description (Optional)
                    </label>
                    <textarea
                      className="w-full p-2 border rounded focus:outline-none focus:ring-2 focus:ring-blue-500"
                      value={newActivityDescription}
                      onChange={(e) => setNewActivityDescription(e.target.value)}
                      placeholder="Enter activity description"
                      rows={2}
                    ></textarea>
                  </div>
                  
                  <div className="flex justify-end">
                    <button
                      type="button"
                      className="mr-2 px-4 py-2 border border-gray-300 rounded-md shadow-sm text-sm font-medium text-gray-700 bg-white hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500"
                      onClick={handleCancelAddActivity}
                    >
                      Cancel
                    </button>
                    <button
                      type="button"
                      className="px-4 py-2 border border-transparent rounded-md shadow-sm text-sm font-medium text-white bg-blue-600 hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 flex items-center"
                      onClick={handleAddNewActivity}
                      disabled={addActivityLoading}
                    >
                      {addActivityLoading ? (
                        <>
                          <Loader2 className="animate-spin h-4 w-4 mr-2" />
                          <span>Saving...</span>
                        </>
                      ) : (
                        <>
                          <Save className="h-4 w-4 mr-2" />
                          <span>Save</span>
                        </>
                      )}
                    </button>
                  </div>
                </div>
              </div>
            )}
          </div>
          
          {/* Add Activity Button */}
          <div className="w-full md:w-1/2 flex items-end">
            <button
              type="button"
              className="w-full px-4 py-2 border border-transparent rounded-md shadow-sm text-sm font-medium text-white bg-green-600 hover:bg-green-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-green-500 flex items-center justify-center"
              onClick={handleAddEquipmentActivity}
              disabled={!selectedActivity || savingChanges}
            >
              {savingChanges ? (
                <>
                  <Loader2 className="animate-spin h-4 w-4 mr-2" />
                  <span>Adding...</span>
                </>
              ) : (
                <>
                  <Plus className="h-4 w-4 mr-2" />
                  <span>Add Activity to Equipment</span>
                </>
              )}
            </button>
          </div>
        </div>
        
        {/* Save Changes Button */}
        {hasChanges && (
          <div className="mb-6">
            <button
              type="button"
              className="w-full px-4 py-3 border border-transparent rounded-md shadow-sm text-sm font-medium text-white bg-blue-600 hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 flex items-center justify-center"
              onClick={handleSaveSequenceChanges}
              disabled={savingChanges}
            >
              {savingChanges ? (
                <>
                  <Loader2 className="animate-spin h-5 w-5 mr-2" />
                  <span>Saving Changes...</span>
                </>
              ) : (
                <>
                  <Save className="h-5 w-5 mr-2" />
                  <span>Save Sequence Changes</span>
                </>
              )}
            </button>
          </div>
        )}
        
        {/* Steps Timeline */}
        {equipmentActivities.length === 0 ? (
          <div className="text-center p-8 bg-white rounded-lg shadow-sm border">
            <p className="text-gray-500">No activities mapped for this equipment</p>
          </div>
        ) : (
          <div className="relative mt-8">
            {/* Timeline center line (visible on md screens and above) */}
            <div className="hidden md:block absolute h-full w-0.5 bg-gray-200 left-1/2 transform -translate-x-1/2"></div>
            
            {equipmentActivities.map((item, index) => {
              // Alternate sides for visual appeal
              const isEven = index % 2 === 1;
              const boxColor = generateBoxColor(index);
              const dotColor = generateDotColor(index);
              
              return (
                <div
                  key={item.id}
                  className="relative mb-6"
                  draggable
                  onDragStart={() => handleDragStart(index)}
                  onDragOver={handleDragOver}
                  onDrop={() => handleDrop(index)}
                >
                  {/* Timeline dot */}
                  {/* <div className={`absolute top-5 left-1/2 transform -translate-x-1/2 md:translate-x-0 md:left-auto ${isEven ? 'md:left-1/2' : 'md:right-1/2'} w-6 h-6 rounded-full ${dotColor} border-4 border-white z-10`}></div> */}
                  
                  <div className={`flex flex-col md:flex-row ${isEven ? 'md:flex-row-reverse' : ''} items-center`}>
                    <div className={`flex w-full md:w-1/2 ${isEven ? 'md:justify-start md:pl-8' : 'md:justify-end md:pr-8'} mb-2 md:mb-0`}>
                      <div className={`rounded-lg ${boxColor} shadow-md border p-4 w-full md:max-w-xs transition-shadow duration-200 hover:shadow-lg cursor-grab`}>
                        <div className="flex items-center mb-2">
                          <div className={`w-8 h-8 rounded-full flex items-center justify-center text-white mr-3 ${dotColor}`}>
                            {item.sequence}
                          </div>
                          <h3 className="font-semibold text-gray-900 flex-grow">{item.equipment_activity.name}</h3>
                          <button 
            className="p-1 hover:bg-gray-200 rounded-md text-red-600 hover:text-red-800 ml-2"
            onClick={(e) => {
              e.stopPropagation();
              setSelectedActivityToDelete(item.id);
              setDeleteDialogVisible(true);
            }}
            disabled={deleteActivityLoading === item.id}
          >
            {deleteActivityLoading === item.id ? (
              <Loader2 className="h-4 w-4 animate-spin" />
            ) : (
              <Trash2 className="h-4 w-4" />
            )}
          </button>
                          {getActivityIcon(item.equipment_activity.name)}
                        </div>
                        
                        <div className="text-sm">
                          <div className="flex justify-between mb-1">
                            <span className="text-gray-500">Equipment:</span>
                            <span className="text-gray-700">{item.equipment.name}</span>
                          </div>
                          {/* <div className="flex justify-between">
                            <span className="text-gray-500">Created:</span>
                            <span className="text-gray-700">{formatDate(item.created_on)}</span>
                          </div> */}
                        </div>
                      </div>
                    </div>
                    <div className="hidden md:block w-1/2"></div>
                  </div>
                </div>
              );
            })}
          </div>
        )}
        
        <div className="text-center mt-8 text-sm text-gray-500">
          <p>Drag steps to reorder them</p>
        </div>
      </div>
    </div>
  );
};

export default EquipmentActivityMapping;